import { z } from "zod";
import { directionToFaceSt, lonLatToDirection, toCube } from "./cube";
import { type BasemapFeatureSetRules, type BasemapGeometry, BasemapName } from "./manifest";
import { transform } from "./tiles";

/**
 * Features as a draft holds them: whole, each with its id, geometry, and property values. A feature
 * set's features are one blob in the draft, its JSON deflated, named by its hash like a tile's
 * pixels. Publishing clips them into the set's tile file (see `feature-tiles.ts`).
 */

/** Positions are whole numbers across a face, from 0 up to this: about 2 mm on Earth. */
export const FACE_UNITS = 2 ** 32;

const Face = z.int().min(0).max(5);
const Across = z
  .int()
  .min(0)
  .max(FACE_UNITS - 1);

/** A point on the world: its face, then how far across the face and how far down it. */
export const BasemapFeaturePosition = z.tuple([Face, Across, Across]).meta({
  id: "BasemapFeaturePosition",
  description:
    "A face, then a whole number from 0 to 2^32 across it from its left side and one down from its top.",
});
export type BasemapFeaturePosition = z.infer<typeof BasemapFeaturePosition>;

/** A line's vertex: its position, and the line's width there in meters. */
const LineVertex = z.tuple([Face, Across, Across, z.number().min(0)]);
export type BasemapLineVertex = z.infer<typeof LineVertex>;

const PropertyValue = z.union([z.string().max(10_000), z.number(), z.boolean()]);
export type BasemapFeatureValue = z.infer<typeof PropertyValue>;

export const BasemapFeature = z
  .strictObject({
    id: z.uuid(),
    geometry: z.discriminatedUnion("type", [
      z.strictObject({
        type: z.literal("line"),
        lines: z.array(z.array(LineVertex).min(2)).min(1).meta({
          description: "One or more lines, each run in the order of its vertices.",
        }),
      }),
      z.strictObject({
        type: z.literal("polygon"),
        polygons: z
          .array(z.array(z.array(BasemapFeaturePosition).min(3)).min(1))
          .min(1)
          .meta({
            description:
              "One or more polygons, each an outer ring then its holes. Rings are open, with their inside on the left: outer rings counterclockwise, holes clockwise.",
          }),
      }),
    ]),
    properties: z.record(BasemapName, PropertyValue),
  })
  .meta({ id: "BasemapFeature", description: "One whole feature, as a draft holds it." });
export type BasemapFeature = z.infer<typeof BasemapFeature>;

export const BasemapFeatures = z.strictObject({ features: z.array(BasemapFeature) });

/** A feature set's whole features as a draft blob: their JSON, deflated. */
export async function featuresToBlob(features: BasemapFeature[]): Promise<Uint8Array> {
  return transform(
    new TextEncoder().encode(JSON.stringify({ features })),
    new CompressionStream("deflate-raw"),
  );
}

export async function blobToFeatures(blob: Uint8Array): Promise<BasemapFeature[]> {
  const bytes = await transform(blob, new DecompressionStream("deflate-raw"));
  return BasemapFeatures.parse(JSON.parse(new TextDecoder().decode(bytes))).features;
}

/** One way a GeoJSON file can't become features: where, and what to change. */
export interface GeoJsonProblem {
  where: string;
  message: string;
}

/** A GeoJSON file as one feature set: its rules, its features, and what it left out. */
export interface GeoJsonFeatureSet {
  rules: BasemapFeatureSetRules;
  features: BasemapFeature[];
  /** Properties past the 64 a set holds, the least used first to go. */
  leftOut: string[];
}

/** The most properties a feature set declares. */
const MOST_PROPERTIES = 64;
/** Importers add vertices so no edge spans more than this many degrees, as edges are great circles. */
const LONGEST_EDGE_DEGREES = 0.5;

/**
 * A GeoJSON file of lines or polygons as one feature set: its geometry type, and its properties
 * from the features' own. Each property's type is the narrowest that holds every value: `boolean`,
 * `int32`, `float64`, then `string`, and a feature without one gets its type's zero. Names are made
 * camelCase. On a sphere, positions are longitude and latitude, and edges gain vertices so that,
 * drawn as great circles, they follow the file's straight lines; outer rings are turned
 * counterclockwise and holes clockwise. On a plane, positions are meters east and north of its
 * southwest corner. Every line takes `widthMeters`. Returns every problem when it can't.
 */
export function featuresFromGeoJson(
  json: unknown,
  geometry: BasemapGeometry,
  { widthMeters }: { widthMeters: number },
): GeoJsonFeatureSet | { problems: GeoJsonProblem[] } {
  const problems: GeoJsonProblem[] = [];
  const found = geoJsonFeatures(json, problems);
  if (!found) return { problems };

  const kinds = new Set<"line" | "polygon">();
  for (const [index, feature] of found.entries()) {
    const type = feature.geometry?.type;
    if (type === "LineString" || type === "MultiLineString") kinds.add("line");
    else if (type === "Polygon" || type === "MultiPolygon") kinds.add("polygon");
    else {
      problems.push({
        where: `features[${index}]`,
        message:
          type === "Point" || type === "MultiPoint"
            ? "It's a point. Feature sets hold lines or polygons; add places as annotations."
            : `Its geometry is ${type ? `a ${type}` : "missing"}. A feature set holds lines or polygons.`,
      });
    }
  }
  if (kinds.size > 1) {
    problems.push({
      where: "features",
      message:
        "It mixes lines and polygons. A feature set holds one geometry type: split the file in two.",
    });
  }
  const [kind] = kinds;
  if (problems.length > 0 || !kind) {
    if (!kind && problems.length === 0) {
      problems.push({ where: "features", message: "It has no features." });
    }
    return { problems };
  }

  const toPosition = positionMaker(geometry);
  const { properties, leftOut, valuesOf } = propertiesOf(found);
  const features: BasemapFeature[] = [];
  found.forEach((feature, index) => {
    const where = `features[${index}]`;
    const coordinates = feature.geometry?.coordinates;
    const id = typeof feature.id === "string" && UUID.test(feature.id) ? feature.id : uuid();
    if (kind === "line") {
      const lines = (
        feature.geometry?.type === "LineString" ? [coordinates] : (coordinates as unknown[])
      ) as unknown[];
      const made: BasemapLineVertex[][] = [];
      for (const [lineIndex, line] of lines.entries()) {
        const points = pointsOf(line, `${where}, line ${lineIndex}`, problems);
        if (!points) continue;
        const dense = geometry.kind === "sphere" ? densify(points, false) : points;
        const vertices: BasemapLineVertex[] = [];
        for (const point of dense) {
          const position = toPosition(point, where, problems);
          if (position) vertices.push([...position, widthMeters]);
        }
        if (vertices.length >= 2) made.push(vertices);
      }
      if (made.length > 0) {
        features.push({
          id,
          geometry: { type: "line", lines: made },
          properties: valuesOf(feature),
        });
      }
      return;
    }
    const polygons = (
      feature.geometry?.type === "Polygon" ? [coordinates] : (coordinates as unknown[])
    ) as unknown[];
    const made: BasemapFeaturePosition[][][] = [];
    for (const [polygonIndex, polygon] of polygons.entries()) {
      if (!Array.isArray(polygon)) continue;
      const rings: BasemapFeaturePosition[][] = [];
      for (const [ringIndex, ring] of polygon.entries()) {
        const at = `${where}, polygon ${polygonIndex}, ring ${ringIndex}`;
        let points = pointsOf(ring, at, problems);
        if (!points) continue;
        const [first] = points;
        const last = points.at(-1);
        if (first && last && first[0] === last[0] && first[1] === last[1]) {
          points = points.slice(0, -1);
        }
        if (points.length < 3) {
          problems.push({ where: at, message: "It has fewer than 3 corners." });
          continue;
        }
        // Outer rings run counterclockwise and holes clockwise, whichever way the file had them.
        const counterclockwise = signedArea(points) > 0;
        if (counterclockwise !== (ringIndex === 0)) points = [...points].reverse();
        const dense = geometry.kind === "sphere" ? densify(points, true) : points;
        const positions: BasemapFeaturePosition[] = [];
        for (const point of dense) {
          const position = toPosition(point, at, problems);
          if (position) positions.push(position);
        }
        if (positions.length >= 3) rings.push(positions);
      }
      if (rings.length > 0) made.push(rings);
    }
    if (made.length > 0) {
      features.push({
        id,
        geometry: { type: "polygon", polygons: made },
        properties: valuesOf(feature),
      });
    }
  });
  if (problems.length > 0) return { problems };
  return { rules: { geometry: kind, properties }, features, leftOut };
}

interface GeoJsonFeature {
  id?: unknown;
  geometry?: { type?: string; coordinates?: unknown } | null;
  properties?: Record<string, unknown> | null;
}

function geoJsonFeatures(json: unknown, problems: GeoJsonProblem[]): GeoJsonFeature[] | null {
  if (!json || typeof json !== "object" || !("type" in json)) {
    problems.push({ where: "file", message: "It isn't GeoJSON: it has no `type`." });
    return null;
  }
  const typed = json as { type: unknown; features?: unknown };
  if (typed.type === "FeatureCollection") {
    if (!Array.isArray(typed.features)) {
      problems.push({ where: "file", message: "Its FeatureCollection has no `features` list." });
      return null;
    }
    return typed.features as GeoJsonFeature[];
  }
  if (typed.type === "Feature") return [json as GeoJsonFeature];
  return [{ geometry: json as GeoJsonFeature["geometry"] }];
}

/** Each property the features have, typed and named, up to the most a set holds. */
function propertiesOf(features: GeoJsonFeature[]) {
  const seen = new Map<string, { count: number; values: unknown[] }>();
  for (const feature of features) {
    for (const [key, value] of Object.entries(feature.properties ?? {})) {
      const entry = seen.get(key) ?? { count: 0, values: [] };
      if (value !== null && value !== undefined) {
        entry.count++;
        entry.values.push(value);
      }
      seen.set(key, entry);
    }
  }
  const ranked = [...seen].sort(([, a], [, b]) => b.count - a.count);
  const kept = ranked.slice(0, MOST_PROPERTIES);
  const leftOut = ranked.slice(MOST_PROPERTIES).map(([key]) => key);
  const taken = new Set<string>();
  const columns = kept.map(([key, { values }]) => {
    let name = camelName(key);
    for (let suffix = 2; taken.has(name); suffix++)
      name = `${camelName(key).slice(0, 60)}${suffix}`;
    taken.add(name);
    return { key, name, type: typeOf(values) };
  });
  const properties: BasemapFeatureSetRules["properties"] = columns.map(({ name, type }) => ({
    name,
    type,
  }));
  const valuesOf = (feature: GeoJsonFeature): Record<string, BasemapFeatureValue> =>
    Object.fromEntries(
      columns.map(({ key, name, type }) => [name, valueAs(type, feature.properties?.[key])]),
    );
  return { properties, leftOut, valuesOf };
}

type PlainType = "boolean" | "int32" | "float64" | "string";

function typeOf(values: unknown[]): PlainType {
  if (values.length > 0 && values.every(value => typeof value === "boolean")) return "boolean";
  if (values.length > 0 && values.every(value => typeof value === "number")) {
    const whole = (values as number[]).every(
      value => Number.isInteger(value) && value >= -(2 ** 31) && value < 2 ** 31,
    );
    return whole ? "int32" : "float64";
  }
  return "string";
}

function valueAs(type: PlainType, value: unknown): BasemapFeatureValue {
  if (type === "boolean") return value === true;
  if (type === "int32" || type === "float64") return typeof value === "number" ? value : 0;
  if (value === null || value === undefined) return "";
  return (typeof value === "string" ? value : JSON.stringify(value)).slice(0, 10_000);
}

/** A GeoJSON property's key as a camelCase basemap name, such as `NAME_EN` to `nameEn`. */
function camelName(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map(word => word.toLowerCase());
  const joined = words
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join("");
  const named = /^[a-z]/.test(joined)
    ? joined
    : `property${joined.charAt(0).toUpperCase()}${joined.slice(1)}`;
  return named.slice(0, 64);
}

type Point = [number, number];

function pointsOf(value: unknown, where: string, problems: GeoJsonProblem[]): Point[] | null {
  if (!Array.isArray(value)) {
    problems.push({ where, message: "Its coordinates aren't a list of positions." });
    return null;
  }
  const points: Point[] = [];
  for (const position of value) {
    const [x, y] = Array.isArray(position) ? position : [];
    if (typeof x !== "number" || typeof y !== "number" || !Number.isFinite(x + y)) {
      problems.push({ where, message: "A position isn't two numbers." });
      return null;
    }
    points.push([x, y]);
  }
  return points;
}

/** Turns a file's position into the world's: longitude and latitude, or meters across a plane. */
function positionMaker(geometry: BasemapGeometry) {
  return (
    [x, y]: Point,
    where: string,
    problems: GeoJsonProblem[],
  ): BasemapFeaturePosition | null => {
    let face: number;
    let s: number;
    let t: number;
    if (geometry.kind === "sphere") {
      if (Math.abs(y) > 90) {
        problems.push({ where, message: `Latitude ${y} is past a pole.` });
        return null;
      }
      ({ face, s, t } = directionToFaceSt(toCube(geometry.orientation, lonLatToDirection(x, y))));
    } else {
      face = 0;
      s = x / geometry.widthMeters;
      t = y / geometry.widthMeters;
      if (s < 0 || s > 1 || t < 0 || t > 1) {
        problems.push({
          where,
          message: `${x}, ${y} is off the plane, which runs 0 to ${geometry.widthMeters} meters each way.`,
        });
        return null;
      }
    }
    return [face, toUnits(s), toUnits(1 - t)];
  };
}

const toUnits = (share: number) =>
  Math.min(FACE_UNITS - 1, Math.max(0, Math.round(share * FACE_UNITS)));

/** Twice a ring's area in degrees, positive when it runs counterclockwise. */
function signedArea(points: Point[]): number {
  let sum = 0;
  const [first] = points;
  if (!first) return 0;
  // Longitudes are read relative to the first, so a ring over the antimeridian keeps its shape.
  const unwrap = (lon: number) => first[0] + ((((lon - first[0]) % 360) + 540) % 360) - 180;
  for (let index = 0; index < points.length; index++) {
    const a = points[index] as Point;
    const b = points[(index + 1) % points.length] as Point;
    sum += unwrap(a[0]) * b[1] - unwrap(b[0]) * a[1];
  }
  return sum;
}

/**
 * Vertices added along each edge, every half degree, so the great circles between them follow the
 * file's straight lines in longitude and latitude. Each edge takes the shorter way round.
 */
function densify(points: Point[], closed: boolean): Point[] {
  const dense: Point[] = [];
  const edges = closed ? points.length : points.length - 1;
  for (let index = 0; index < edges; index++) {
    const a = points[index] as Point;
    const b = points[(index + 1) % points.length] as Point;
    const dLon = ((((b[0] - a[0]) % 360) + 540) % 360) - 180;
    const dLat = b[1] - a[1];
    const steps = Math.max(
      1,
      Math.ceil(Math.max(Math.abs(dLon), Math.abs(dLat)) / LONGEST_EDGE_DEGREES),
    );
    for (let step = 0; step < steps; step++) {
      dense.push([a[0] + (dLon * step) / steps, a[1] + (dLat * step) / steps]);
    }
  }
  if (!closed) dense.push(points.at(-1) as Point);
  return dense;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const uuid = () => crypto.randomUUID();
